/**
 * Cookie Policy Page
 * Route: /cookies
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, CheckCircle2, XCircle } from 'lucide-react';
import SEO from '@/components/common/SEO';

const CookiePolicy: React.FC = () => {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  const cookieTypes = [
    {
      name: 'Essential Cookies',
      icon: <CheckCircle2 className="h-5 w-5 text-green-500" />,
      required: true,
      description: 'These cookies are necessary for the website to function and cannot be switched off. They are set in response to actions you take such as logging in, filling in forms, or setting your privacy preferences.',
      examples: ['Session authentication token', 'CSRF protection token', 'User preferences', 'Shopping cart contents'],
    },
    {
      name: 'Analytics Cookies',
      icon: analyticsEnabled ? <CheckCircle2 className="h-5 w-5 text-green-500" /> : <XCircle className="h-5 w-5 text-muted-foreground" />,
      required: false,
      enabled: analyticsEnabled,
      onToggle: () => setAnalyticsEnabled(!analyticsEnabled),
      description: 'These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our Platform. They help us understand which pages are the most and least popular.',
      examples: ['Page view counts', 'Session duration', 'Bounce rate', 'User flow through the site'],
    },
    {
      name: 'Marketing Cookies',
      icon: marketingEnabled ? <CheckCircle2 className="h-5 w-5 text-purple-500" /> : <XCircle className="h-5 w-5 text-muted-foreground" />,
      required: false,
      enabled: marketingEnabled,
      onToggle: () => setMarketingEnabled(!marketingEnabled),
      description: 'These cookies may be set through our Platform by our advertising partners to build a profile of your interests and show you relevant adverts on other sites.',
      examples: ['Ad targeting data', 'Retargeting pixels', 'Conversion tracking', 'Social media integration'],
    },
  ];

  const sections = [
    {
      title: 'What Are Cookies?',
      content: `Cookies are small text files that are stored on your computer or mobile device when you visit a website. They allow websites to remember your actions and preferences over a period of time, so you don't have to keep re-entering them whenever you come back to the site or browse from one page to another.`,
    },
    {
      title: 'How We Use Cookies',
      content: `MemAds uses cookies to: keep you signed in to your account, remember your city and search preferences, understand how you use our platform (page views, clicks, etc.), improve the relevance of ads shown, detect fraudulent activity, and ensure the security of your account. We also use local storage and session storage for some of these purposes.`,
    },
    {
      title: 'Third-Party Cookies',
      content: `Some cookies are placed by third-party services that appear on our pages. We use services such as Google Analytics to understand usage patterns. These third parties may use cookies, web beacons, and similar technologies to collect information about your use of our Platform and other websites. This information may be used to, among other things, evaluate the use of the website and provide services relating to website activity and internet usage.`,
    },
    {
      title: 'Managing Cookies',
      content: `Most web browsers allow you to control cookies through their settings preferences. However, if you limit the ability of websites to set cookies, you may worsen your overall user experience and be unable to access certain features of MemAds. You can also opt-out of analytics tracking by using browser extensions such as the Google Analytics Opt-out Browser Add-on. Note that disabling essential cookies will break the Platform's core functionality including login and ad posting.`,
    },
    {
      title: 'Cookie Duration',
      content: `Session cookies expire when you close your browser. Persistent cookies remain on your device for the duration specified in the cookie (e.g., 30 days, 1 year). Authentication cookies are typically valid for 30 days. Analytics cookies may persist for up to 2 years. You can clear all cookies at any time through your browser settings.`,
    },
    {
      title: 'Updates to This Policy',
      content: `We may update this Cookie Policy from time to time to reflect changes in technology, regulation, or our business practices. We will post any changes on this page and, where appropriate, notify you by email or through the Platform. We encourage you to check this page periodically.`,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Cookie Policy"
        description="Read the Cookie Policy for MemAds. Learn how we use cookies and tracking technologies to improve your experience."
        canonical="/cookies"
      />
      {/* Page Header */}
      <div className="border-b border-border bg-secondary/30">
        <div className="mx-auto max-w-4xl px-4 py-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary text-primary-foreground">
              <Cookie className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground">Cookie Policy</h1>
              <p className="text-sm text-muted-foreground">Last updated: June 2025</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-4xl px-4 py-10 space-y-6">

        {/* Cookie Preferences Card */}
        <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6">
          <h2 className="font-heading text-lg font-semibold text-foreground mb-1">Your Cookie Preferences</h2>
          <p className="text-sm text-muted-foreground mb-5">Manage how MemAds uses cookies on your device. Essential cookies cannot be disabled as they are required for the Platform to work.</p>
          <div className="space-y-4">
            {cookieTypes.map((type) => (
              <div key={type.name} className="flex items-start gap-4 rounded-xl border border-border bg-card p-4">
                <div className="mt-0.5">{type.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="font-semibold text-sm text-foreground">{type.name}</h3>
                    {type.required ? (
                      <span className="shrink-0 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">Always On</span>
                    ) : (
                      <button
                        onClick={type.onToggle}
                        id={`cookie-toggle-${type.name.replace(/\s+/g, '-').toLowerCase()}`}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                          type.enabled ? 'bg-primary' : 'bg-muted'
                        }`}
                        aria-label={`Toggle ${type.name}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            type.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{type.description}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {type.examples.map((ex) => (
                      <span key={ex} className="rounded-md bg-secondary px-2 py-0.5 text-xs text-muted-foreground">{ex}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content Sections */}
        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
          <div className="space-y-8">
            {sections.map((section) => (
              <div key={section.title} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h2 className="font-heading text-lg font-semibold text-foreground mb-3">{section.title}</h2>
                <p className="text-muted-foreground leading-relaxed text-sm">{section.content}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Related Links */}
        <div className="flex flex-wrap gap-3 text-sm">
          <span className="text-muted-foreground">Related:</span>
          <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
          <span className="text-border">·</span>
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
          <span className="text-border">·</span>
          <a href="mailto:privacy@memads.in" className="text-primary hover:underline">Contact Us</a>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
